import { lazy, type LazyExoticComponent, type ComponentType } from "react";

type GameBoardProps = {
  sessionId: string;
  playerId: string;
  roomCode: string;
};

type LazyGameBoard = LazyExoticComponent<ComponentType<GameBoardProps>>;

const GAME_BOARD_REGISTRY: Record<
  string,
  () => Promise<{ default: ComponentType<GameBoardProps> }>
> = {
  "terrible-psychiatrist": () =>
    import("./terrible_psychiatrist/TerriblePsychiatristBoard").then((m) => ({
      default: m.TerriblePsychiatristBoard,
    })),
  reaction: () =>
    import("./Reaction").then((m) => ({
      default: m.ReactionBoard,
    })),
  "peer-review": () =>
    import("./peer_review/PeerReviewBoard").then((m) => ({
      default: m.PeerReviewBoard,
    })),
  cipher: () =>
    import("./cipher").then((m) => ({
      default: m.CipherBoard,
    })),
  warp: () =>
    import("./warp").then((m) => ({
      default: m.WarpBoard,
    })),
  "swarm-think": () =>
    import("./swarm_think").then((m) => ({
      default: m.SwarmThinkBoard,
    })),
  "sick-day": () =>
    import("./sick_day/SickDayBoard").then((m) => ({
      default: m.SickDayBoard,
    })),
  "debate-club": () =>
    import("./debate_club/DebateClubBoard").then((m) => ({
      default: m.DebateClubBoard,
    })),
  "hot-potato-heist": () =>
    import("./hot_potato_heist/HotPotatoHeistBoard").then((m) => ({
      default: m.HotPotatoHeistBoard,
    })),
  "liars-hand": () =>
    import("./liars_hand/LiarsHandBoard").then((m) => ({
      default: m.LiarsHandBoard,
    })),
  "faux-facts": () =>
    import("./faux_facts").then((m) => ({
      default: m.FauxFactsBoard,
    })),
  redaction: () =>
    import("./redaction").then((m) => ({
      default: m.RedactionBoard,
    })),
  imagined: () =>
    import("./imagined").then((m) => ({
      default: m.ImaginedBoard,
    })),
  "dial-in": () =>
    import("./dial_in/DialInBoard").then((m) => ({
      default: m.DialInBoard,
    })),
  "sinky-boat": () =>
    import("./SinkyBoat").then((m) => ({
      default: m.SinkyBoatBoard,
    })),
  "out-of-spec": () =>
    import("./out_of_spec/OutOfSpecBoard").then((m) => ({
      default: m.OutOfSpecBoard,
    })),
  "undef-character": () =>
    import("./undef_character/UndefCharacterBoard").then((m) => ({
      default: m.UndefCharacterBoard,
    })),
  "per-diem-quest": () =>
    import("./per_diem_quest/PDQBoard").then((m) => ({
      default: m.PDQBoard,
    })),
};

const lazyBoardCache: Record<string, LazyGameBoard> = {};

export function hasGameBoard(gameId: string): boolean {
  return gameId in GAME_BOARD_REGISTRY;
}

export function getGameBoard(gameId: string): LazyGameBoard | null {
  if (!hasGameBoard(gameId)) {
    return null;
  }

  if (!lazyBoardCache[gameId]) {
    const loader = GAME_BOARD_REGISTRY[gameId];
    lazyBoardCache[gameId] = lazy(loader);
  }

  return lazyBoardCache[gameId];
}
